import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { ChevronDown, ChevronUp, FolderPlus, HelpCircle, ShieldCheck, SlidersHorizontal, X } from 'lucide-react'
import './Settings.scss'
import Sidebar from '../../components/Sidebar/Sidebar'
import { drumtaleAdminSessionAtom } from '../../AtomStore'
import { gradeAccessPolicy, openPageSpecification } from '../shared/pageSpecification'
import { settingsDataModelGuide, settingsImplementationGuide, settingsSpecification } from './settingsSpecification'

const TOAST_DURATION_MS = 2400

type SettingsModalKey = 'difficulty' | 'scoreCategories' | 'gradePermission'

interface BilingualItem {
  key: string
  label: string
  ko: string
  en: string
}

interface CategoryItem {
  ko: string
  en: string
}

const INITIAL_DIFFICULTY_ITEMS: BilingualItem[] = [
  { key: 'easy', label: 'Easy 표시명', ko: '이지', en: 'Easy' },
  { key: 'normal', label: 'Normal 표시명', ko: '노멀', en: 'Normal' },
  { key: 'hard', label: 'Hard 표시명', ko: '하드', en: 'Hard' },
]

const INITIAL_GRADE_ITEMS: BilingualItem[] = [
  { key: 'basic', label: '일반 등급명', ko: '일반', en: 'Basic' },
  { key: 'drummer', label: '드러머 등급명', ko: '드러머', en: 'Drummer' },
  { key: 'cognitive', label: '인지 등급명', ko: '인지', en: 'Cognitive' },
  { key: 'plus', label: '플러스 등급명', ko: '플러스', en: 'Plus' },
]

const INITIAL_CATEGORIES: CategoryItem[] = [
  { ko: '트로트', en: 'Trot' },
  { ko: '가요', en: 'K-Pop' },
  { ko: '동요', en: 'Kids' },
  { ko: '팝', en: 'Pop' },
]

const GRADE_PERMISSIONS: Record<string, { drum: boolean; cognitive: boolean }> = {
  basic: { drum: false, cognitive: false },
  drummer: { drum: true, cognitive: false },
  cognitive: { drum: false, cognitive: true },
  plus: { drum: true, cognitive: true },
}

const MODAL_META: Record<SettingsModalKey, { eyebrow: string; title: string }> = {
  difficulty: { eyebrow: '난이도 수정', title: '난이도명 수정' },
  gradePermission: { eyebrow: '등급 및 권한 설정', title: '등급명 및 권한 수정' },
  scoreCategories: { eyebrow: '악보 카테고리', title: '악보 카테고리 관리' },
}

function updateBilingualDraft(
  setDraft: React.Dispatch<React.SetStateAction<BilingualItem[]>>,
  index: number,
  field: 'ko' | 'en',
  value: string,
) {
  setDraft((current) => current.map((item, i) => (i === index ? { ...item, [field]: value } : item)))
}

function Settings() {
  const navigate = useNavigate()
  const isAdminSessionActive = useAtomValue(drumtaleAdminSessionAtom)

  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  const [difficultyItems, setDifficultyItems] = useState<BilingualItem[]>(INITIAL_DIFFICULTY_ITEMS)
  const [gradeItems, setGradeItems] = useState<BilingualItem[]>(INITIAL_GRADE_ITEMS)
  const [categories, setCategories] = useState<CategoryItem[]>(INITIAL_CATEGORIES)

  const [activeModal, setActiveModal] = useState<SettingsModalKey | null>(null)
  const [draftDifficultyItems, setDraftDifficultyItems] = useState<BilingualItem[]>([])
  const [draftGradeItems, setDraftGradeItems] = useState<BilingualItem[]>([])
  const [draftCategories, setDraftCategories] = useState<CategoryItem[]>([])
  const [newCategoryKo, setNewCategoryKo] = useState('')
  const [newCategoryEn, setNewCategoryEn] = useState('')

  useEffect(() => {
    if (!isAdminSessionActive) {
      navigate('/')
    }
  }, [isAdminSessionActive, navigate])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current)
      }
    }
  }, [])

  const showToast = (message: string) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
    }
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  const openSettingsModal = (key: SettingsModalKey) => {
    if (key === 'difficulty') {
      setDraftDifficultyItems(difficultyItems.map((item) => ({ ...item })))
    } else if (key === 'gradePermission') {
      setDraftGradeItems(gradeItems.map((item) => ({ ...item })))
    } else if (key === 'scoreCategories') {
      setDraftCategories(categories.map((category) => ({ ...category })))
      setNewCategoryKo('')
      setNewCategoryEn('')
    }
    setActiveModal(key)
  }

  const closeSettingsModal = () => setActiveModal(null)

  const handleSaveDifficulty = () => {
    if (draftDifficultyItems.some((item) => !item.ko.trim() || !item.en.trim())) {
      showToast('한글명과 영문명을 모두 입력해 주세요.')
      return
    }
    setDifficultyItems(draftDifficultyItems.map((item) => ({ ...item, ko: item.ko.trim(), en: item.en.trim() })))
    setActiveModal(null)
    showToast('설정을 저장했습니다.')
  }

  const handleSaveGradePermission = () => {
    if (draftGradeItems.some((item) => !item.ko.trim() || !item.en.trim())) {
      showToast('한글명과 영문명을 모두 입력해 주세요.')
      return
    }
    setGradeItems(draftGradeItems.map((item) => ({ ...item, ko: item.ko.trim(), en: item.en.trim() })))
    setActiveModal(null)
    showToast('등급 및 권한 설정을 저장했습니다.')
  }

  const handleDeleteDraftCategory = (index: number) => {
    setDraftCategories((current) => current.filter((_, i) => i !== index))
  }

  const handleMoveDraftCategory = (index: number, direction: 'up' | 'down') => {
    setDraftCategories((current) => {
      const targetIndex = direction === 'up' ? index - 1 : index + 1
      if (targetIndex < 0 || targetIndex >= current.length) {
        return current
      }
      const next = [...current]
      ;[next[index], next[targetIndex]] = [next[targetIndex], next[index]]
      return next
    })
  }

  const handleSaveCategories = () => {
    if (draftCategories.some((category) => !category.ko.trim() || !category.en.trim())) {
      showToast('카테고리 한글명과 영문명을 모두 입력해 주세요.')
      return
    }
    const trimmedCategories = draftCategories.map((category) => ({ ko: category.ko.trim(), en: category.en.trim() }))
    const koNames = trimmedCategories.map((category) => category.ko)
    if (new Set(koNames).size !== koNames.length) {
      showToast('중복된 카테고리 한글명은 사용할 수 없습니다.')
      return
    }

    let finalCategories = trimmedCategories
    const newKo = newCategoryKo.trim()
    const newEn = newCategoryEn.trim()
    if (newKo || newEn) {
      if (!newKo || !newEn) {
        showToast('신규 카테고리의 한글명과 영문명을 모두 입력해 주세요.')
        return
      }
      if (koNames.includes(newKo)) {
        showToast('이미 등록된 카테고리입니다.')
        return
      }
      finalCategories = [...trimmedCategories, { ko: newKo, en: newEn }]
    }

    if (finalCategories.length === 0) {
      showToast('카테고리를 최소 1개 이상 등록해 주세요.')
      return
    }

    setCategories(finalCategories)
    setActiveModal(null)
    showToast('악보 카테고리를 저장했습니다.')
  }

  const handleSubmitModal = () => {
    if (activeModal === 'difficulty') handleSaveDifficulty()
    else if (activeModal === 'gradePermission') handleSaveGradePermission()
    else if (activeModal === 'scoreCategories') handleSaveCategories()
  }

  const handleOpenSpecification = () => {
    const opened = openPageSpecification(settingsSpecification, settingsDataModelGuide, settingsImplementationGuide, {
      gradeAccessPolicy,
    })
    if (!opened) {
      showToast('화면 사양서 창을 열 수 없습니다. 팝업 차단을 해제해 주세요.')
    }
  }

  const settingsCards = [
    {
      key: 'difficulty' as const,
      icon: SlidersHorizontal,
      title: '난이도 수정',
      description: difficultyItems.map((item) => `${item.ko} / ${item.en}`).join(' · '),
    },
    {
      key: 'scoreCategories' as const,
      icon: FolderPlus,
      title: '악보 카테고리 관리',
      description: categories.map((category) => `${category.ko} / ${category.en}`).join(' · '),
    },
    {
      key: 'gradePermission' as const,
      icon: ShieldCheck,
      title: '등급 및 권한 설정',
      description: '등급명(한글/영문), 드럼·인지 권한',
    },
  ]

  return (
    <div className='Settings'>
      <div className='app-shell'>
        <Sidebar />

        <main className='workspace'>
          <header className='topbar'>
            <div className='title-area'>
              <h1>설정</h1>
            </div>
          </header>

          <section className='view active'>
            <div className='settings-action-grid'>
              {settingsCards.map(({ key, icon: Icon, title, description }) => (
                <button
                  key={key}
                  className='settings-action-card'
                  type='button'
                  onClick={() => openSettingsModal(key)}
                >
                  <Icon size={22} />
                  <strong>{title}</strong>
                  <span>{description}</span>
                </button>
              ))}
            </div>
          </section>
        </main>
      </div>

      {activeModal && (
        <div className='modal-backdrop' role='presentation' onClick={closeSettingsModal}>
          <section
            className='modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='settingsModalTitle'
            onClick={(event) => event.stopPropagation()}
          >
            <div className='modal-header'>
              <div>
                <p className='eyebrow'>{MODAL_META[activeModal].eyebrow}</p>
                <h2 id='settingsModalTitle'>{MODAL_META[activeModal].title}</h2>
              </div>
              <button className='icon-only' type='button' aria-label='닫기' onClick={closeSettingsModal}>
                <X size={18} />
              </button>
            </div>

            <div className='modal-body'>
              {activeModal === 'difficulty' && (
                <>
                  <div className='modal-grid'>
                    {draftDifficultyItems.map((item, index) => (
                      <React.Fragment key={item.key}>
                        <label className='modal-field'>
                          {item.label} (한글)
                          <input
                            value={item.ko}
                            onChange={(event) => updateBilingualDraft(setDraftDifficultyItems, index, 'ko', event.target.value)}
                            required
                          />
                        </label>
                        <label className='modal-field'>
                          {item.label} (영문)
                          <input
                            value={item.en}
                            onChange={(event) => updateBilingualDraft(setDraftDifficultyItems, index, 'en', event.target.value)}
                            required
                          />
                        </label>
                      </React.Fragment>
                    ))}
                  </div>
                  <p className='modal-help'>저장하면 관리자 화면의 표시명에 반영됩니다.</p>
                </>
              )}

              {activeModal === 'gradePermission' && (
                <>
                  <div className='modal-section'>
                    <h3>등급명</h3>
                    <div className='modal-grid'>
                      {draftGradeItems.map((item, index) => (
                        <React.Fragment key={item.key}>
                          <label className='modal-field'>
                            {item.label} (한글)
                            <input
                              value={item.ko}
                              onChange={(event) => updateBilingualDraft(setDraftGradeItems, index, 'ko', event.target.value)}
                              required
                            />
                          </label>
                          <label className='modal-field'>
                            {item.label} (영문)
                            <input
                              value={item.en}
                              onChange={(event) => updateBilingualDraft(setDraftGradeItems, index, 'en', event.target.value)}
                              required
                            />
                          </label>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  <div className='modal-section'>
                    <h3>등급별 권한</h3>
                    <div className='permission-modal-list'>
                      {draftGradeItems.map((item) => {
                        const permission = GRADE_PERMISSIONS[item.key]
                        return (
                          <div className='permission-modal-row' key={item.key}>
                            <div><strong>{item.ko}</strong></div>
                            <div className='permission-checks'>
                              <label className='toggle'>
                                <input type='checkbox' defaultChecked={permission.drum} />
                                드럼
                              </label>
                              <label className='toggle'>
                                <input type='checkbox' defaultChecked={permission.cognitive} />
                                인지
                              </label>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  <p className='modal-help'>저장하면 관리자 화면의 표시명에 반영됩니다.</p>
                </>
              )}

              {activeModal === 'scoreCategories' && (
                <>
                  <div className='modal-section'>
                    <h3>신규 카테고리 등록</h3>
                    <div className='category-add-row'>
                      <label className='modal-field'>
                        카테고리명(한글)
                        <input
                          value={newCategoryKo}
                          onChange={(event) => setNewCategoryKo(event.target.value)}
                          placeholder='예: 재즈'
                        />
                      </label>
                      <label className='modal-field'>
                        카테고리명(영문)
                        <input
                          value={newCategoryEn}
                          onChange={(event) => setNewCategoryEn(event.target.value)}
                          placeholder='예: Jazz'
                        />
                      </label>
                    </div>
                  </div>
                  <div className='modal-section'>
                    <h3>기존 카테고리 수정, 순서 변경 및 삭제</h3>
                    <div className='category-modal-list'>
                      {draftCategories.map((category, index) => (
                        <div className='category-modal-row' key={`${index}-${category.ko}`}>
                          <label className='modal-field'>
                            카테고리명(한글)
                            <input
                              value={category.ko}
                              onChange={(event) => setDraftCategories((current) => current.map((row, i) => (i === index ? { ...row, ko: event.target.value } : row)))}
                              required
                            />
                          </label>
                          <label className='modal-field'>
                            카테고리명(영문)
                            <input
                              value={category.en}
                              onChange={(event) => setDraftCategories((current) => current.map((row, i) => (i === index ? { ...row, en: event.target.value } : row)))}
                              required
                            />
                          </label>
                          <div className='category-row-actions'>
                            <button
                              className='icon-button'
                              type='button'
                              disabled={index === 0}
                              aria-label={`${category.ko} 위로 이동`}
                              title='위로 이동'
                              onClick={() => handleMoveDraftCategory(index, 'up')}
                            >
                              <ChevronUp size={15} />
                            </button>
                            <button
                              className='icon-button'
                              type='button'
                              disabled={index === draftCategories.length - 1}
                              aria-label={`${category.ko} 아래로 이동`}
                              title='아래로 이동'
                              onClick={() => handleMoveDraftCategory(index, 'down')}
                            >
                              <ChevronDown size={15} />
                            </button>
                          </div>
                          <button className='secondary-button danger' type='button' onClick={() => handleDeleteDraftCategory(index)}>
                            삭제
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className='modal-help'>카테고리를 삭제하면 해당 카테고리의 곡은 모두 미공개 처리됩니다.</p>
                  </div>
                </>
              )}
            </div>

            <div className='modal-footer'>
              <button className='secondary-button' type='button' onClick={closeSettingsModal}>취소</button>
              <button className='primary-button' type='button' onClick={handleSubmitModal}>저장</button>
            </div>
          </section>
        </div>
      )}

      <button
        className='page-spec-button'
        type='button'
        aria-label='현재 화면 사양서 열기'
        title='현재 화면 사양서'
        onClick={handleOpenSpecification}
      >
        <HelpCircle size={22} />
      </button>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default Settings
